import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Platform, TextInput, type TextInputProps } from 'react-native';

type Props = TextInputProps & { minHeight?: number };

type MeasurableNode = {
  style: { height: string };
  scrollHeight: number;
  offsetHeight: number;
  clientHeight: number;
};

/**
 * Height a web textarea needs to show all of its text. A textarea's
 * scrollHeight never drops below its current height, so feeding it straight
 * back into the height grows the box on every render (React error #185).
 * Collapsing the height first makes the result depend on the text alone.
 */
export function measureTextareaHeight(node: MeasurableNode): number {
  const previous = node.style.height;
  node.style.height = '0px';
  const borders = node.offsetHeight - node.clientHeight;
  const height = Math.ceil(node.scrollHeight + borders);
  node.style.height = previous;
  return height;
}

/**
 * Multiline input that wraps long text onto new lines and grows with it.
 * Native inputs grow on their own; a web textarea keeps a fixed height, so on
 * web the height follows the measured content.
 */
export function AutoGrowTextInput({
  minHeight = 40,
  style,
  onContentSizeChange,
  onLayout,
  ...props
}: Props) {
  const inputRef = useRef<TextInput>(null);
  const layoutWidth = useRef(0);
  const [webHeight, setWebHeight] = useState(minHeight);

  const resizeWeb = useCallback(() => {
    const node = inputRef.current as unknown as MeasurableNode | null;
    if (Platform.OS !== 'web' || !node || typeof node.scrollHeight !== 'number') return;
    const next = Math.max(minHeight, measureTextareaHeight(node));
    setWebHeight(current => (current === next ? current : next));
  }, [minHeight]);

  // Saved values arrive through `value` without a change event, so measure
  // after each new value as well as while typing.
  useLayoutEffect(resizeWeb, [resizeWeb, props.value]);

  return (
    <TextInput
      {...props}
      ref={inputRef}
      multiline
      scrollEnabled={false}
      textAlignVertical="top"
      onContentSizeChange={event => {
        resizeWeb();
        onContentSizeChange?.(event);
      }}
      onLayout={event => {
        // Wrapping depends on width, so re-measure when the width changes.
        const width = event.nativeEvent.layout.width;
        if (width !== layoutWidth.current) {
          layoutWidth.current = width;
          resizeWeb();
        }
        onLayout?.(event);
      }}
      style={[
        style,
        { minHeight },
        Platform.OS === 'web' ? { height: webHeight } : null,
      ]}
    />
  );
}
